import { initialState as notify_initial_state } from "$lib/Notify/notify.js";
import { flushSaves, stopSaving } from "./store";

// Kept when saved content is cleared: settings and screen state, which hold
// no client content. Every other key in this site's storage is removed.
const OWNER_KEY = "saved_content_owner";

const SETTINGS_KEYS = new Set([
    OWNER_KEY,
    "isCleared",
    "source_review_highlights",
    "tour_active",
    "tour_step",
    "cp_config_tour_shows",
    "current_selected_model",
    "project_sorter_draft_model",
    "defend_page_selected_tab",
    "open_tab_index_draft_reasoner_sa",
    "download_options",
    "superadmin-selected-tab",
    "admin-jobs-state",
]);
const SETTINGS_PREFIXES = ["draft_mode:", "onboarding_seen_"];

// Also kept by Log out: some users keep their writing style in instructions.
// Card and toolbar instructions live inside global_state and Notify's inside
// its own store; both are trimmed to their instructions below.
const INSTRUCTION_KEYS = new Set([
    "draft_reasoner_user_project_instructions",
    "draft_reasoner_user_core_activity_instructions",
    "draft_reasoner_user_supporting_activity_instructions",
    "user_instructions_to_regenerate_paragraph",
    "project_description_general_prompt",
]);

// Removes the content saved in this browser. The caller must reload the page
// next, because the stores still hold the old content in memory.
export function clearSavedContent({ keep_instructions }) {
    // Write what is still queued, so instructions typed in the last second
    // are kept, then stop saving until the reload.
    flushSaves();
    stopSaving();
    try {
        const kept_state = keep_instructions ? instructionsOnly(readSaved("global_state")) : null;
        const kept_notify = keep_instructions ? notifyInstructionsOnly(readSaved("notify")) : null;
        for (const key of Object.keys(localStorage)) {
            if (SETTINGS_KEYS.has(key) || SETTINGS_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
            if (keep_instructions && INSTRUCTION_KEYS.has(key)) continue;
            localStorage.removeItem(key);
        }
        if (kept_state) localStorage.setItem("global_state", JSON.stringify(kept_state));
        if (kept_notify) localStorage.setItem("notify", JSON.stringify(kept_notify));
    } catch (e) {
        console.error("Could not clear saved content", e);
    }
}

// The content saved in this browser belongs to the user who saved it. When a
// different user signs in, it is all cleared, instructions included. Returns
// true when it was cleared; the caller must then load the page again.
export function claimSavedContent(user) {
    if (!user?.id) return false;
    try {
        const owner = localStorage.getItem(OWNER_KEY);
        const cleared = owner !== null && owner !== String(user.id);
        if (cleared) clearSavedContent({ keep_instructions: false });
        localStorage.setItem(OWNER_KEY, String(user.id));
        return cleared;
    } catch (e) {
        return false;
    }
}

function readSaved(key) {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch (e) {
        return null;
    }
}

// Only the parts of the page state saved under an "instructions" key, which
// is where both card and toolbar instructions live.
function instructionsOnly(node) {
    if (!node || typeof node !== "object") return null;
    let kept = null;
    for (const [key, value] of Object.entries(node)) {
        const part = key === "instructions" ? value : instructionsOnly(value);
        if (part != null) {
            kept = kept ?? {};
            kept[key] = part;
        }
    }
    return kept;
}

// Notify keeps each project description option's instructions; the claim
// dates, answers and generated descriptions go back to the defaults.
function notifyInstructionsOnly(notify) {
    const options = Object.entries(notify?.project_description ?? {});
    if (!options.length) return null;
    const kept = structuredClone(notify_initial_state);
    for (const [option, value] of options) {
        if (!kept.project_description[option]) continue;
        for (const field of ["user_instructions", "prompt"]) {
            if (value?.[field]) kept.project_description[option][field] = value[field];
        }
    }
    return kept;
}
